(() => {
  if (customElements.get('zenova-testimonials')) return;

  class ZenovaTestimonials extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-zr-track]');
      this.slides = Array.from(this.querySelectorAll('[data-zr-slide]'));
      this.dots = this.querySelector('[data-zr-dots]');
      this.previous = this.querySelector('[data-zr-previous]');
      this.next = this.querySelector('[data-zr-next]');
      this.status = this.querySelector('[data-zr-status]');
      if (!this.track || !this.slides.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.go(this.current - 1), options);
      this.next.addEventListener('click', () => this.go(this.current + 1), options);
      this.dots.addEventListener('click', (event) => {
        const button = event.target.closest('[data-zr-page]');
        if (button) this.go(Number(button.dataset.zrPage));
      }, options);
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
        const index = { ArrowLeft: this.current - 1, ArrowRight: this.current + 1, Home: 0, End: this.stops.length - 1 }[event.key];
        if (index === undefined) return;
        event.preventDefault();
        this.go(index);
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-zr-slide]');
        if (!slide) return;
        const left = slide.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft);
        const index = this.stops.reduce((best, stop, i) => Math.abs(stop - left) < Math.abs(this.stops[best] - left) ? i : best, 0);
        this.go(index);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.measure();
    }

    measure() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const padding = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      this.stops = [];
      this.slides.forEach((slide) => {
        const stop = Math.max(0, Math.min(maximum, slide.offsetLeft - padding));
        if (!this.stops.length || Math.abs(stop - this.stops[this.stops.length - 1]) > 2) this.stops.push(stop);
      });
      this.dots.replaceChildren(...this.stops.map((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zr__dot';
        button.dataset.zrPage = index;
        button.setAttribute('aria-label', `Show testimonials, position ${index + 1} of ${this.stops.length}`);
        button.setAttribute('aria-controls', this.track.id);
        return button;
      }));
      const single = this.stops.length < 2;
      this.dots.hidden = single;
      this.previous.hidden = single;
      this.next.hidden = single;
      this.update();
    }

    update() {
      this.current = this.stops.reduce((best, stop, index) => Math.abs(stop - this.track.scrollLeft) < Math.abs(this.stops[best] - this.track.scrollLeft) ? index : best, 0);
      this.dots.querySelectorAll('button').forEach((button, index) => {
        button.setAttribute('aria-current', String(index === this.current));
      });
      this.previous.disabled = this.current === 0;
      this.next.disabled = this.current === this.stops.length - 1;
    }

    go(index) {
      const page = Math.max(0, Math.min(index, this.stops.length - 1));
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
      this.track.scrollTo({ left: this.stops[page], behavior });
      this.status.textContent = `Testimonials, position ${page + 1} of ${this.stops.length}`;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }

  customElements.define('zenova-testimonials', ZenovaTestimonials);
})();
